let audioCtx: AudioContext | null = null;
let muted = false;

export function setMuted(value: boolean): void {
  muted = value;
}

export function isMuted(): boolean {
  return muted;
}

function getContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new AudioContext();
  }
  if (audioCtx.state === "suspended") {
    audioCtx.resume();
  }
  return audioCtx;
}

function createNoiseBuffer(ctx: AudioContext, duration: number): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const length = Math.floor(sampleRate * duration);
  const buffer = ctx.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
}

function playNoiseBurst(
  ctx: AudioContext,
  startTime: number,
  duration: number,
  frequency: number,
  qFactor: number,
  volume: number,
  filterType: BiquadFilterType = "bandpass",
): void {
  const noiseBuffer = createNoiseBuffer(ctx, duration);
  const source = ctx.createBufferSource();
  source.buffer = noiseBuffer;

  const filter = ctx.createBiquadFilter();
  filter.type = filterType;
  filter.frequency.value = frequency;
  filter.Q.value = qFactor;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(volume, startTime);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

  source.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);

  source.start(startTime);
  source.stop(startTime + duration);
}

export function playDiceRoll(): void {
  if (muted) return;
  const ctx = getContext();
  const now = ctx.currentTime;

  // Series of rapid noise bursts simulating dice bouncing on wood
  const burstCount = 8 + Math.floor(Math.random() * 4);
  for (let i = 0; i < burstCount; i++) {
    const time = now + i * 0.04 + Math.random() * 0.015;
    const freq = 1800 + Math.random() * 1200;
    const vol = 0.15 * (1 - i / burstCount) + Math.random() * 0.05;
    playNoiseBurst(ctx, time, 0.03 + Math.random() * 0.02, freq, 3, vol);
  }

  // Final settling tap
  const settleTime = now + burstCount * 0.04 + 0.05;
  playNoiseBurst(ctx, settleTime, 0.06, 1200, 2, 0.12);
}

export function playCheckerPlace(): void {
  if (muted) return;
  const ctx = getContext();
  const now = ctx.currentTime;

  // Sharp wooden clack — high-frequency noise burst with fast decay
  playNoiseBurst(ctx, now, 0.05, 2200 + Math.random() * 400, 4, 0.2);

  // Lower-frequency resonance body (wood thud)
  playNoiseBurst(ctx, now, 0.08, 600 + Math.random() * 200, 2, 0.1, "lowpass");
}

export function playBearOff(): void {
  if (muted) return;
  const ctx = getContext();
  const now = ctx.currentTime;

  // Soft sliding noise with downward frequency sweep
  const noiseBuffer = createNoiseBuffer(ctx, 0.2);
  const source = ctx.createBufferSource();
  source.buffer = noiseBuffer;

  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.setValueAtTime(3000, now);
  filter.frequency.exponentialRampToValueAtTime(400, now + 0.18);
  filter.Q.value = 2;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.12, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

  source.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);

  source.start(now);
  source.stop(now + 0.2);

  // Gentle high "lift" tone
  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(800, now);
  osc.frequency.exponentialRampToValueAtTime(1200, now + 0.15);

  const oscGain = ctx.createGain();
  oscGain.gain.setValueAtTime(0.04, now);
  oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);

  osc.connect(oscGain);
  oscGain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.15);
}

/**
 * Soft AFK chime — fires when the in-game overlay's AFK timer expires on the
 * local player's turn. Two ascending sine notes, distinct from
 * dice/piece/game-over so it reads as "your turn, friend" rather than "event".
 */
export function playChime(): void {
  if (muted) return;
  const ctx = getContext();
  const now = ctx.currentTime;
  const notes = [880, 1174.66]; // A5 → D6, perfect-fourth
  const noteDuration = 0.32;
  for (let i = 0; i < notes.length; i += 1) {
    const freq = notes[i];
    if (freq === undefined) continue;
    const time = now + i * noteDuration * 0.7;
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.06, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + noteDuration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(time);
    osc.stop(time + noteDuration);
  }
}

/**
 * "Your turn" signal — fires once when the turn passes to the local player in
 * a game where somebody else was acting (vs-AI or online). Deliberately
 * quieter and shorter than `playChime`, which is the *nag* that fires after
 * you've already sat on your turn: this one only says the wait is over, so a
 * soft two-note rise with a marimba-ish triangle body keeps it from competing
 * with the move sounds it lands next to.
 */
export function playTurnStart(): void {
  if (muted) return;
  const ctx = getContext();
  const now = ctx.currentTime;
  const notes = [587.33, 880]; // D5 → A5, perfect-fifth
  const noteDuration = 0.22;
  for (let i = 0; i < notes.length; i += 1) {
    const freq = notes[i];
    if (freq === undefined) continue;
    const time = now + i * 0.11;
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    // Short attack ramp — a hard gate on a triangle wave clicks.
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(0.05, time + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.001, time + noteDuration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(time);
    osc.stop(time + noteDuration);
  }
}

/**
 * Play a one-shot audio file via `HTMLAudioElement`. Used for pre-recorded
 * voice clips (e.g. AFK ping reminder) that don't fit the Web Audio synth
 * style of the other helpers in this module. Respects the global mute flag.
 * Silently swallows the play promise rejection so callers don't need to
 * handle the autoplay-blocked `NotAllowedError`.
 */
export function playAudioSample(
  url: string,
  options?: { volume?: number },
): void {
  if (muted) return;
  const audio = new Audio(url);
  if (options?.volume !== undefined) {
    audio.volume = Math.min(1, Math.max(0, options.volume));
  }
  const playback = audio.play();
  if (playback !== undefined) {
    playback.catch(() => {
      /* swallow autoplay/NotAllowedError — caller can't recover */
    });
  }
}

export function playGameOver(): void {
  if (muted) return;
  const ctx = getContext();
  const now = ctx.currentTime;

  // Short ascending melodic sequence (3 notes)
  const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
  const noteDuration = 0.18;

  for (let i = 0; i < notes.length; i++) {
    const freq = notes[i];
    if (freq === undefined) continue;
    const time = now + i * noteDuration;

    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = freq;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.1, time);
    gain.gain.setValueAtTime(0.1, time + noteDuration * 0.7);
    gain.gain.exponentialRampToValueAtTime(0.001, time + noteDuration);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(time);
    osc.stop(time + noteDuration);
  }

  // Final sustained chord
  const chordTime = now + notes.length * noteDuration;
  const chordFreqs = [523.25, 659.25, 783.99]; // C major chord
  for (const freq of chordFreqs) {
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = freq;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.08, chordTime);
    gain.gain.exponentialRampToValueAtTime(0.001, chordTime + 0.6);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(chordTime);
    osc.stop(chordTime + 0.6);
  }
}
